// ══════════════════════════════════════════════════════════════
// COMPASS – Error Page Component
// ══════════════════════════════════════════════════════════════
import { ChangeDetectionStrategy, Component, DestroyRef, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule, Location } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { take } from 'rxjs/operators';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatCardModule } from '@angular/material/card';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AppState } from '../../core/store/app.state';
import { UserActions } from '../../core/store/user/user.actions';
import { selectUserPermissions } from '../../core/store/user/user.selectors';
import { Permission } from '../../core/models/permission.config';

@Component({
  selector: 'app-error-page',
  standalone: true,
  imports: [CommonModule, MatButtonModule, MatIconModule, MatCardModule, TranslateModule],
  styleUrl: './error-page.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="error-page">
      <mat-card class="error-card">
        <mat-card-header>
          <mat-icon class="error-icon">error_outline</mat-icon>
          <mat-card-title>{{ errorTitle() }}</mat-card-title>
        </mat-card-header>
        <mat-card-content>
          <p class="error-message">{{ errorMessage() }}</p>
          @if (attemptedPath()) {
            <p class="error-path">
              <strong>{{ 'ERROR.ATTEMPTED_PATH' | translate }}:</strong> {{ attemptedPath() }}
            </p>
          }
        </mat-card-content>
        <mat-card-actions>
          @if (requiresLogout()) {
            <button mat-raised-button color="warn" (click)="logout()">
              {{ 'ERROR.LOGOUT_AND_RETURN' | translate }}
            </button>
          } @else if (isForbidden()) {
            <button mat-raised-button color="primary" (click)="goToMainPage()">
              {{ 'ERROR.GO_TO_MAIN_PAGE' | translate }}
            </button>
          } @else if (loadFailed()) {
            <button mat-raised-button color="primary" (click)="tryAgain()">
              {{ 'ERROR.TRY_AGAIN' | translate }}
            </button>
          } @else {
            <button mat-raised-button color="primary" (click)="goBack()">
              {{ 'ERROR.GO_BACK' | translate }}
            </button>
            <button mat-raised-button (click)="goHome()">
              {{ 'ERROR.GO_HOME' | translate }}
            </button>
          }
        </mat-card-actions>
      </mat-card>
    </div>
  `,
})
export class ErrorPageComponent {
  private readonly location = inject(Location);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly translate = inject(TranslateService);
  private readonly store = inject(Store<AppState>);
  private readonly destroyRef = inject(DestroyRef);

  readonly errorTitle = signal('');
  readonly errorMessage = signal('');
  readonly attemptedPath = signal('');
  readonly requiresLogout = signal(false);
  readonly isProtectedRoute = signal(false);
  readonly isForbidden = signal(false);
  /** The page's code couldn't be downloaded (connection or server): offer to try again, not to log out. */
  readonly loadFailed = signal(false);

  constructor() {
    // Get error details from route data
    this.route.queryParams.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(params => {
      this.attemptedPath.set(params['path'] || '');
      this.requiresLogout.set(params['logout'] === 'true');
      this.isProtectedRoute.set(params['protected'] === 'true');
      // The page is reused when only its query changes: the previous error's buttons must not carry over.
      this.isForbidden.set(false);
      this.loadFailed.set(false);
      const errorType = params['type'] || 'not-found';

      switch (errorType) {
        case 'not-found':
          this.errorTitle.set(this.translate.instant('ERROR.PAGE_NOT_FOUND'));
          if (this.requiresLogout()) {
            if (this.isProtectedRoute()) {
              this.errorMessage.set(this.translate.instant('ERROR.PAGE_NOT_FOUND_PROTECTED_MESSAGE'));
            } else {
              this.errorMessage.set(this.translate.instant('ERROR.PAGE_NOT_FOUND_LOGOUT_MESSAGE'));
            }
          } else {
            this.errorMessage.set(this.translate.instant('ERROR.PAGE_NOT_FOUND_MESSAGE'));
          }
          break;
        case 'unauthorized':
          this.errorTitle.set(this.translate.instant('ERROR.UNAUTHORIZED'));
          if (this.requiresLogout()) {
            this.errorMessage.set(this.translate.instant('ERROR.UNAUTHORIZED_LOGOUT_MESSAGE'));
          } else {
            this.errorMessage.set(this.translate.instant('ERROR.UNAUTHORIZED_MESSAGE'));
          }
          break;
        case 'forbidden':
          this.isForbidden.set(true);
          this.errorTitle.set(this.translate.instant('ERROR.FORBIDDEN'));
          this.errorMessage.set(this.translate.instant('ERROR.FORBIDDEN_MESSAGE'));
          break;
        case 'no-permissions':
          this.isForbidden.set(true);
          this.errorTitle.set(this.translate.instant('ERROR.NO_PERMISSIONS'));
          this.errorMessage.set(this.translate.instant('ERROR.NO_PERMISSIONS_MESSAGE'));
          break;
        case 'load-failed':
          this.loadFailed.set(true);
          this.errorTitle.set(this.translate.instant('ERROR.LOAD_FAILED'));
          this.errorMessage.set(this.translate.instant('ERROR.LOAD_FAILED_MESSAGE'));
          break;
        default:
          this.errorTitle.set(this.translate.instant('ERROR.GENERIC_ERROR'));
          this.errorMessage.set(this.translate.instant('ERROR.GENERIC_ERROR_MESSAGE'));
      }
    });
  }

  goBack(): void {
    this.location.back();
  }

  goHome(): void {
    this.router.navigate(['/']);
  }

  goToMainPage(): void {
    this.store.select(selectUserPermissions).pipe(take(1)).subscribe((permissions) => {
      const hasCompanyAccess =
        permissions.includes(Permission.PROCESS_READ) ||
        permissions.includes(Permission.FUND_READ) ||
        permissions.includes(Permission.INVITATION_READ);
      this.router.navigate([hasCompanyAccess ? '/company' : '/']);
    });
  }

  /**
   * A full load of the page that failed, so its code is downloaded again. Only a path in this app: the path comes
   * from the address bar, so anything else (another site) opens the start page instead.
   */
  tryAgain(): void {
    const path = this.attemptedPath();
    let sameApp = false;
    try {
      sameApp = path.startsWith('/') && new URL(path, window.location.origin).origin === window.location.origin;
    } catch {
      // Not a valid address (e.g. '//['): the start page.
    }
    this.reloadPage(sameApp ? path : '/');
  }

  /** A full page load of `url` (an in-app URL). Its own method, so specs can stand in for it. */
  protected reloadPage(url: string): void {
    window.location.assign(url);
  }

  logout(): void {
    // Dispatch logout action which will clear session and redirect to login
    this.store.dispatch(UserActions.logout());
  }
}
